import React, { useState } from 'react';
import { api } from '../lib/api';
import { coerceUrl } from '../utils/url';

interface AddTrackableLinkFormProps {
  orgId: string;
  isOpen: boolean;
  onClose: () => void;
  onLinkAdded: (createdLink?: { uid: string; trackableLink: string }) => void;
  // Pre-fill values when the form is opened from the lead-magnet flow.
  initialSmLink?: string;
  initialDestinationUrl?: string;
  initialOffer?: string;
  // Soft warning shown above the destination input when backend validation
  // couldn't find a Typeform on the URL. Doesn't block submit.
  destinationWarning?: string | null;
}

const AddTrackableLinkForm: React.FC<AddTrackableLinkFormProps> = ({
  orgId,
  isOpen,
  onClose,
  onLinkAdded,
  initialSmLink,
  initialDestinationUrl,
  initialOffer,
  destinationWarning
}) => {
  const [smLink, setSmLink] = useState('');
  const [destinationUrl, setDestinationUrl] = useState('');
  const [offer, setOffer] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);

  // Reset form and message when modal opens. Pre-fills from props win over
  // empty defaults so the lead-magnet flow can hand the user values to confirm.
  React.useEffect(() => {
    if (isOpen) {
      setSmLink(initialSmLink || '');
      setDestinationUrl(initialDestinationUrl || '');
      setOffer(initialOffer || '');
      setMessage('');
      setGeneratedLink(null);
      setLoading(false);
    }
  }, [isOpen, initialSmLink, initialDestinationUrl, initialOffer]);

  // Handle click outside to close modal
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setGeneratedLink(null);

    try {
      // Validate required fields
      if (!smLink.trim() || !destinationUrl.trim()) {
        setMessage('Social Media Link and Destination URL are required');
        setLoading(false);
        return;
      }

      // Coerce + validate URLs (prepends https:// when missing).
      const coercedSmLink = coerceUrl(smLink);
      const coercedDestination = coerceUrl(destinationUrl);
      if (!coercedSmLink || !coercedDestination) {
        setMessage('Please enter valid URLs');
        setLoading(false);
        return;
      }

      const payload = {
        orgId,
        smLink: coercedSmLink,
        destinationUrl: coercedDestination,
        offer: offer.trim() || undefined
      };

      const response = await api.trackableLinks.add(payload);

      if (response.success) {
        setGeneratedLink(response.trackableLink.trackableLink);
        setMessage('✅ Trackable link created successfully! Click the Copy button or manually close when ready.');
        setSmLink('');
        setOffer('');
        // Don't auto-close - let user manually close after copying
        onLinkAdded({
          uid: response.trackableLink.uid,
          trackableLink: response.trackableLink.trackableLink
        });
      } else {
        setMessage(`❌ Error: ${response.error || 'Failed to create link'}`);
      }
    } catch (error: any) {
      setMessage(`❌ Error: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      setMessage('✅ Link copied to clipboard!');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 3100
      }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '550px',
        width: '90%',
        maxHeight: '80vh',
        overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#1a1a1a' }}>Create Trackable Link</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <p style={{ color: '#374151', marginBottom: '16px' }}>
            Create a trackable link with a unique ID. The link will be automatically generated with your organization's next sequential code.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>
              Social Media Link *
            </label>
            <input
              type="url"
              value={smLink}
              onChange={(e) => setSmLink(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>
              The YouTube or social media URL you want to track
            </p>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>
              Destination URL *
            </label>
            {destinationWarning && (
              <div style={{
                marginBottom: '8px',
                padding: '10px 12px',
                backgroundColor: '#FFFBEB',
                border: '1px solid #FDE68A',
                borderRadius: '6px',
                fontSize: '12px',
                color: '#92400E',
                lineHeight: 1.4
              }}>
                <strong style={{ display: 'block', marginBottom: 2 }}>Heads up</strong>
                {destinationWarning}
              </div>
            )}
            <input
              type="url"
              value={destinationUrl}
              onChange={(e) => setDestinationUrl(e.target.value)}
              placeholder="https://www.thedigitalromeo.com"
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>
              Where users will be redirected when they click the trackable link
            </p>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>
              Offer (Optional)
            </label>
            <input
              type="text"
              value={offer}
              onChange={(e) => setOffer(e.target.value)}
              placeholder="e.g., Free Consultation, 20% Off"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>
              Optional: Associate this link with a specific offer or campaign
            </p>
          </div>

          {generatedLink && (
            <div style={{
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px',
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe'
            }}>
              <p style={{ fontWeight: '500', marginBottom: '8px', color: '#1e40af' }}>
                Generated Trackable Link:
              </p>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <code style={{
                  flex: 1,
                  padding: '8px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  fontSize: '13px',
                  wordBreak: 'break-all'
                }}>
                  {generatedLink}
                </code>
                <button
                  type="button"
                  onClick={copyToClipboard}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Copy
                </button>
              </div>
            </div>
          )}

          {message && (
            <div style={{
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px',
              backgroundColor: message.includes('✅') ? '#f0fdf4' : '#fef2f2',
              border: `1px solid ${message.includes('✅') ? '#bbf7d0' : '#fecaca'}`,
              color: message.includes('✅') ? '#166534' : '#dc2626',
              fontSize: '14px'
            }}>
              {message}
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f3f4f6',
                color: '#374151',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Creating...' : 'Create Link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTrackableLinkForm;
